import type { FC, ReactNode } from "react"
import React from "react"
import type { IconProps, ComponentWithAs } from "@chakra-ui/react"
import { Box, Flex, Stack, Text, Icon as ChakraIcon } from "@chakra-ui/react"
import { isAddress } from "@ethersproject/address"
import dayjs from "dayjs"

import type { Account, Block } from "query/graphql"
import UserAvatar from "common/components/UserAvatar"
import Link from "common/components/Link"
import { DATE_FORMATS } from "common/constants/date"
import { ROUTES } from "common/constants/routes"
import { shortString, subString } from "common/helpers/string"
import { TimelineState } from "common/types/timeline"
import { getDisplayName } from "user/helpers/user"

const Icon: FC<
  {
    state: TimelineState
    as: ComponentWithAs<"svg", IconProps>
    isShowingDecorativeLine?: boolean
  } & IconProps
> = ({ state, isShowingDecorativeLine = false, ...iconProps }) => {
  const getBackground = (state: TimelineState) => {
    switch (state) {
      case TimelineState.Active: {
        return "purple.600"
      }
      case TimelineState.Disabled: {
        return "white"
      }
      case TimelineState.Past: {
        return "white"
      }
      default: {
        return "white"
      }
    }
  }

  const getFill = (state: TimelineState) => {
    switch (state) {
      case TimelineState.Active: {
        return "white"
      }
      case TimelineState.Disabled: {
        return "gray.400"
      }
      case TimelineState.Past: {
        return "gray.700"
      }
      default: {
        return "gray.400"
      }
    }
  }

  const getBorder = (state: TimelineState) => {
    switch (state) {
      case TimelineState.Active: {
        return "2px solid hsla(246, 66%, 56%, 1)"
      }
      case TimelineState.Disabled: {
        return "2px solid hsla(214, 20%, 69%, 1)"
      }
      case TimelineState.Past: {
        return "2px solid hsla(218, 17%, 35%, 1)"
      }
      default: {
        return "2px solid hsla(214, 20%, 69%, 1)"
      }
    }
  }

  return (
    <Flex
      align="center"
      bg={getBackground(state)}
      border={getBorder(state)}
      borderRadius="50%"
      h={8}
      justify="center"
      p={2.5}
      pos="relative"
      w={8}
    >
      <ChakraIcon
        color={getFill(state)}
        fill={getFill(state)}
        h={3}
        w={3}
        {...iconProps}
      />
      {isShowingDecorativeLine ? (
        <Box
          bg="gray.300"
          h="46px"
          pos="absolute"
          right="48%"
          top="30px"
          w="1px"
        />
      ) : null}
    </Flex>
  )
}

const Date: FC<{
  shouldRender?: boolean
  timestamp?: string
  format?: string
}> = ({ shouldRender = true, timestamp, format = DATE_FORMATS.timeline }) => {
  if (!timestamp || !shouldRender) return null

  return (
    <Text color="gray.500" textStyle="body.regular.sm">
      {dayjs(timestamp).format(format)}
    </Text>
  )
}

const Title: FC<{ state: TimelineState; children: ReactNode }> = ({
  state,
  children,
}) => {
  const getTitleColor = (state: TimelineState): string => {
    switch (state) {
      case TimelineState.Active: {
        return "purple.600"
      }
      case TimelineState.Disabled: {
        return "gray.400"
      }
      case TimelineState.Past: {
        return "gray.700"
      }
      default: {
        return "gray.700"
      }
    }
  }

  return (
    <Text color={getTitleColor(state)} textStyle="body.bold.lg" w="max-content">
      {children}
    </Text>
  )
}

const User: FC<{
  state: TimelineState
  account?: Pick<Account, "name" | "address" | "picture"> | null
  slug: string
}> = ({ state, account, slug }) => {
  if (state === TimelineState.Disabled || !account) return null

  const { address, picture } = account
  const endpoint = ROUTES.governance.delegate.profile(slug, address)
  const displayName = getDisplayName(account)

  return (
    <Stack align="center" direction="row" spacing={2}>
      <UserAvatar address={address} size="xs" src={picture} />
      <Link href={endpoint}>
        <Text
          className="clamp-text-1"
          color="gray.500"
          textStyle="body.regular.sm"
        >
          {isAddress(displayName)
            ? shortString(displayName)
            : subString(displayName, 26)}
        </Text>
      </Link>
    </Stack>
  )
}

const Estimation: FC<{
  block?: Pick<Block, "timestamp">
  customText?: string
}> = ({ block, customText }) => {
  if (customText) {
    return (
      <Text
        className="clamp-text-1"
        color="gray.500"
        textStyle="body.regular.sm"
      >
        {customText}
      </Text>
    )
  }

  if (!block) return null

  const { timestamp } = block

  if (!timestamp) return null

  // If now is before the block timestamp, then display the time since now up to that block timestamp
  // Otherwise, display nothing
  const date = dayjs().isBefore(dayjs(block.timestamp))
    ? dayjs().to(dayjs(block.timestamp))
    : null

  if (!date) {
    return null
  }

  return (
    <Text className="clamp-text-1" color="gray.500" textStyle="body.regular.sm">
      {date}
    </Text>
  )
}

const Container: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Stack align="center" direction="row" h="3.75rem" spacing={3}>
      {children}
    </Stack>
  )
}

const Timeline = { Date, Title, User, Container, Icon, Estimation }

export default Timeline
